import { compare, validate } from 'compare-versions'
import type { ChallengeStatus } from './focus-challenge-model'

// Remote per-cluster config file (special-challenge-config.json). The file is
// cluster-specific — the URL is selected by UserInfo.server — so activity_id
// is a flat number, not a Record<Server, number>. See CONTEXT.md#cluster.
//
// All fields are required: a file missing any of them fails closed and hides
// the whole feature, card included (CONTEXT.md#special-challenge-限時挑戰).
export type SpecialChallengeConfig = {
  activity_id: number
  // Minimum app version that may see the entry at all: below it the card
  // (and with it the detail view) is hidden, the hub is otherwise untouched.
  required_app_version: string
  // Countdown target for the upcoming state. Display-only: the progresses
  // endpoint returns nothing before the backend valid window opens, so the
  // start time can't come from the API.
  valid_start_time: string
  visible_start_time: string
  // Grace-period end: after valid_range.upper the reward stays claimable
  // until this moment, but progress no longer accumulates.
  visible_end_time: string
  // Detail-view hero: an image, drawn cover-fit over main_page_bg (a CSS
  // background value, e.g. a linear-gradient()).
  main_page_visual_url: string
  main_page_bg: string
  // Per-event copy lives in the native string catalog, not web Lokalise —
  // these carry the catalog keys. The description key takes [%1$d] = target
  // minutes and [%2$s] = the reward name, resolved from
  // tree_type_{reward_tree_type_gid}_title.
  main_page_title_key: string
  main_page_description_key: string
  reward_tree_type_gid: number
  // The reward sticker: the entry card's visual and the icon at the end of
  // the detail view's progress bar.
  reward_sticker_img_url: string
  // Per-event theming. The color pairs carry bare colors — the entry card
  // owns the gradient direction and stops; the *_bg / *_color fields are
  // full CSS background values (solid color, linear-/radial-gradient()).
  entry_bg_gradient_from_to: [string, string]
  entry_border_gradient_from_to: [string, string]
  progress_bar_bg_color: string
  progress_bg: string
  claim_reward_btn_bg: string
  reward_reveal_bg: string
}

const REQUIRED_STRING_FIELDS = [
  'valid_start_time',
  'visible_start_time',
  'visible_end_time',
  'main_page_visual_url',
  'main_page_bg',
  'main_page_title_key',
  'main_page_description_key',
  'reward_sticker_img_url',
  'progress_bar_bg_color',
  'progress_bg',
  'claim_reward_btn_bg',
  'reward_reveal_bg',
] as const

const REQUIRED_COLOR_PAIR_FIELDS = [
  'entry_bg_gradient_from_to',
  'entry_border_gradient_from_to',
] as const

function isColorPair(value: unknown): value is [string, string] {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    value.every((color) => typeof color === 'string' && color !== '')
  )
}

export function isCompleteSpecialChallengeConfig(
  config: Partial<SpecialChallengeConfig> | null | undefined,
): config is SpecialChallengeConfig {
  if (!config) return false
  if (typeof config.activity_id !== 'number') return false
  if (typeof config.reward_tree_type_gid !== 'number') return false
  // Format-checked beyond non-empty: compare() throws on a malformed version,
  // so an ops typo must fail closed here, not crash the hub downstream.
  if (
    typeof config.required_app_version !== 'string' ||
    !validate(config.required_app_version)
  )
    return false
  if (!REQUIRED_COLOR_PAIR_FIELDS.every((field) => isColorPair(config[field])))
    return false
  return REQUIRED_STRING_FIELDS.every(
    (field) => typeof config[field] === 'string' && config[field] !== '',
  )
}

// Subset of the activity progress row this card reads. The activity-scoped
// challenge carries valid_range (the focus-progresses family does not).
export type SpecialChallengeProgress = {
  id: number
  progress: number
  target: number
  status: ChallengeStatus
  challenge_difficulty: {
    challenge: {
      valid_range: { lower: string; upper: string } | null
    }
  }
}

export type ActivityProgressesResponse = {
  challenge_progresses: SpecialChallengeProgress[]
}

export type SpecialChallengeBar = 'progress' | 'claim' | 'complete'

export type SpecialChallengeCardState =
  | { kind: 'hidden' }
  | { kind: 'upcoming'; countdownTo: string }
  | {
      kind: 'active' | 'grace'
      bar: SpecialChallengeBar
      progressId: number
      progress: number
      target: number
      countdownTo: string
    }

export type VisibleSpecialChallengeCard = Exclude<
  SpecialChallengeCardState,
  { kind: 'hidden' }
>

function classifyBar(status: ChallengeStatus): SpecialChallengeBar {
  if (status === 'reward_delivered') return 'complete'
  if (status === 'completed') return 'claim'
  return 'progress'
}

// Which countdown label a surface shows. Grace always reads as reward expiry
// ("Expires in N days"), claimed or not, on the card and the detail view
// alike — the two surfaces never disagree.
export type SpecialChallengeTimerKind = 'start' | 'ends' | 'expires'

export function specialTimerKind(
  kind: 'upcoming' | 'active' | 'grace',
): SpecialChallengeTimerKind {
  if (kind === 'upcoming') return 'start'
  if (kind === 'grace') return 'expires'
  return 'ends'
}

// Days shown by "Expires in [%d] days": rounded up, so a partial day still
// counts and the label never reads 0 while the state is visible.
export function expiresInDays(totalSeconds: number): number {
  return Math.ceil(totalSeconds / 86400)
}

// The card fails closed: no config, a failed or unexpectedly empty progress
// fetch, and unparseable dates all hide it (the hub never degrades, and
// deleting the config file doubles as a kill switch).
export function deriveSpecialChallengeCard(input: {
  // Partial: the remote file is unvalidated JSON — an incomplete one fails
  // closed here rather than degrading downstream.
  config: Partial<SpecialChallengeConfig> | null
  // null = the progress fetch failed or has not resolved.
  progresses: SpecialChallengeProgress[] | null
  now: number
  // True once a claim succeeded in this page session: keeps the card visible
  // (Completed) through the grace period instead of yanking it mid reward
  // flow. On the next page entry the grace-period rule hides it again.
  claimedThisSession: boolean
  // Asserted non-null at startup (lib/params.ts) — the WebView URL always
  // carries app_version.
  appVersion: string
}): SpecialChallengeCardState {
  const { config, progresses, now, claimedThisSession, appVersion } = input
  const hidden = { kind: 'hidden' } as const
  if (!isCompleteSpecialChallengeConfig(config)) return hidden
  if (compare(appVersion, config.required_app_version, '<')) return hidden

  const visibleStart = Date.parse(config.visible_start_time)
  const visibleEnd = Date.parse(config.visible_end_time)
  if (Number.isNaN(visibleStart) || Number.isNaN(visibleEnd)) return hidden
  if (now < visibleStart || now >= visibleEnd) return hidden

  if (!progresses) return hidden

  const p = progresses[0]
  if (!p) {
    // The backend returns an empty list before the valid window opens.
    const validStart = Date.parse(config.valid_start_time)
    if (Number.isNaN(validStart) || now >= validStart) return hidden
    return { kind: 'upcoming', countdownTo: config.valid_start_time }
  }

  const upper = p.challenge_difficulty.challenge.valid_range?.upper
  if (upper == null) return hidden
  const validEnd = Date.parse(upper)
  if (Number.isNaN(validEnd)) return hidden

  const base = {
    progressId: p.id,
    progress: p.progress,
    target: p.target,
  }

  if (now < validEnd) {
    return {
      kind: 'active',
      bar: classifyBar(p.status),
      ...base,
      countdownTo: upper,
    }
  }

  // Grace period: progress is frozen; only reaching the target and not having
  // claimed keeps the card around.
  if (p.status === 'completed') {
    return {
      kind: 'grace',
      bar: 'claim',
      ...base,
      countdownTo: config.visible_end_time,
    }
  }
  if (p.status === 'reward_delivered' && claimedThisSession) {
    return {
      kind: 'grace',
      bar: 'complete',
      ...base,
      countdownTo: config.visible_end_time,
    }
  }
  return hidden
}
